import { apiFetch } from "@/lib/api"
import { API_BASE } from "@/lib/config"

/**
 * The contract layer: /v1/legal/*. One read gives the page everything —
 * what is in force and what is outstanding — because the re-consent banner
 * needs an answer on every load and a request per document would make that
 * the most expensive thing on the page.
 */

export type DocumentScope = "account" | "product" | "commercial"
export type ChangeType = "material" | "non_material"

export interface AcceptedRecord {
  versionId: number
  versionLabel: string
  acceptedAt: string
  userId: number | null
  signerName: string | null
  signerTitle: string | null
  method: "clickwrap" | "esignature"
  pdfUrl: string | null
}

export interface LegalDocument {
  code: string
  name: string
  plainLanguageSummary: string | null
  scope: DocumentScope
  productCode: string | null
  requiresSignature: boolean
  currentVersion: { id: number; label: string; effectiveFrom: string } | null
  accepted: AcceptedRecord | null
}

export interface OutstandingItem {
  code: string
  name: string
  scope: DocumentScope
  productCode: string | null
  changeType: ChangeType
  summary: string | null
  versionId: number
  firstSeen: boolean
  deadlineTs: number | null
  overdue: boolean
}

export interface SignerIdentity {
  /** From the account, not from a form: nobody signs under another person's name. */
  name: string | null
  title: string | null
}

export interface StandingConsent {
  consentCode: string
  name: string
  summary: string | null
  /** What withdrawing it does — shown before it is withdrawn, not after. */
  withdrawal: string | null
  grantedAt: string
  /** The product that stops if this is withdrawn, per Schedule C 5.3. */
  endsProduct: string | null
}

/** A product activation somebody started and did not finish. */
export interface PendingActivation {
  productCode: string
  total: number
  done: number
  complete: boolean
}

export interface LegalState {
  documents: LegalDocument[]
  activatedProducts: string[]
  consents: StandingConsent[]
  pendingActivations: PendingActivation[]
  outstanding: OutstandingItem[]
  requiresAction: boolean
  nearestDeadline: number | null
  restriction: { products: string[]; account: boolean }
  maySign: boolean
  signer: SignerIdentity
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await apiFetch(`${API_BASE}/legal${path}`, init)
  const body = await res.json().catch(() => null)

  if (!res.ok) {
    // The accept endpoint answers a badly-rendered document with the list of
    // faults; surface the first one rather than a generic failure.
    const errors = (body as { errors?: Record<string, string[]> } | null)?.errors
    const first = errors ? Object.values(errors).flat()[0] : undefined
    throw new Error(first || `Request failed (${res.status})`)
  }

  return body as T
}

export function fetchLegalState(): Promise<LegalState> {
  return request("/documents")
}

export interface AcceptInput {
  versionId: number
  /** The exact wording the person agreed to — stored verbatim as evidence. */
  affirmationText: string
  /** A claim about the signer's role. Their name is never sent: the server
   *  takes it from the account so it cannot be someone else's. */
  signerTitle?: string
  authorityAffirmed: boolean
}

export function acceptDocument(input: AcceptInput): Promise<{ acceptanceId: number; acceptedAt: string }> {
  return request("/documents/accept", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      versionId: input.versionId,
      affirmationText: input.affirmationText,
      signerTitle: input.signerTitle || "",
      authorityAffirmed: input.authorityAffirmed ? 1 : 0,
    }),
  })
}

export function grantConsent(
  consentCode: string,
  screenVersion: string
): Promise<{ consentId: number; alreadyGranted: boolean }> {
  return request("/consents", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ consentCode, screenVersion }),
  })
}

export function withdrawConsent(consentCode: string): Promise<{ withdrawn: boolean; endsProduct: string | null }> {
  return request(`/consents/${encodeURIComponent(consentCode)}`, { method: "DELETE" })
}

export interface DocumentBody {
  code: string
  name: string
  versionId: number
  versionLabel: string
  effectiveFrom: string
  changeSummary: string | null
  /** True when this is the customer's own accepted copy rather than the template. */
  accepted: boolean
  body: string
}

export function fetchDocumentBody(code: string, versionId?: number): Promise<DocumentBody> {
  const query = versionId ? `?versionId=${versionId}` : ""
  return request(`/documents/${encodeURIComponent(code)}/body${query}`)
}

/**
 * Turning a product on. The server returns separate steps rather than a bundle
 * because the card data-sharing consent has to be its own screen with its own
 * affirmative act, and the card partner's and issuing bank's agreements are
 * contracts with them rather than with us. Each step is completed by its own
 * request; there is no call that completes several at once.
 */
export type ActivationStepKind = "clickwrap" | "third_party" | "consent"

export interface ActivationStep {
  kind: ActivationStepKind
  code: string
  name: string
  summary: string | null
  /** Set on third-party steps: who the customer is actually contracting with. */
  counterparty: string | null
  /** Where a third party's text lives — we do not hold it. */
  externalUrl: string | null
  versionId: number | null
  versionLabel: string | null
  /** Consent steps only: the exact words beside the box, stored as evidence. */
  affirmation: string | null
  /** Consent steps only: what withdrawing it later does. */
  withdrawal: string | null
  satisfied: boolean
  /** Nothing is published for this step, so nobody can complete it. */
  blocked: boolean
}

export interface ActivationPackage {
  productCode: string
  steps: ActivationStep[]
  complete: boolean
  blocked: string[]
  consentScreenVersion: string
  maySign: boolean
}

export function fetchActivationPackage(productCode: string): Promise<ActivationPackage> {
  return request(`/activation/${encodeURIComponent(productCode)}`)
}
